import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { UserLocalStorageService } from 'app/permission/services/user.local.storage.service';
import { Constants } from 'app/shared/constants';
import { LogService } from 'app/shared/log/log.service';
import { Page } from 'app/shared/models/page';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { Observable } from 'rxjs/Observable';
import { map } from 'rxjs/operators/map';
import { FlashMessagesService } from '../../shared/flash-messages/service/flash.messages.service';
import { EventCollisionService } from '../event-collision/event-collision.service';
import { MatchingProperty } from '../models/matching-property';
import { MatchingPropertyAssign } from '../models/matching-property-assign';
import { PropertyMatch } from '../models/property-match';
import { AuthenticationStore } from './../../permission/services/authentication.store';
import { MatchingPropertyHttpService } from './matching-property.http.service';
import { PagesizeType } from './pagination/pagesize-type';

@Injectable()
export class MatchingPropertyStore {

  private propertyPage: BehaviorSubject<Page<MatchingProperty>> = new BehaviorSubject(Page.empty<MatchingProperty>());

  constructor(
    private matchingPropertyHttpService: MatchingPropertyHttpService,
    private flashMessagesService: FlashMessagesService,
    private logService: LogService,
    private translateService: TranslateService,
    private authenticationStore: AuthenticationStore,
    private userLocalStorageService: UserLocalStorageService,
    private eventCollisionService: EventCollisionService
  ) {
    this.init();
  }

  private init(): void {
    if (this.authenticationStore.isSupportUser()) {
      this.getMatchingProperties(
        Constants.SEARCH_DEFAULT_PAGE,
        this.userLocalStorageService.getPagesize(PagesizeType.MATCHING_PROPERTY),
        Constants.DEFAULT_MATCHING_PROPERTY_FIELD_SORT_FIELD,
        null,
        Constants.SORT_DIRECTION_ASC
      ).take(1).subscribe();
    }
  }

  get properties(): Observable<Page<MatchingProperty>> {
    return this.propertyPage.asObservable();
  }

  get totalPropertyCount(): Observable<number> {
    return this.propertyPage.asObservable().map(page => page.totalElements);
  }

  getMatchingProperties(page: number, size: number, sortField: string, secondarySortField: string, sortDirection: string): Observable<any> {
    let sort = 'sort=' + sortField + ',' + sortDirection;
    if (secondarySortField) {
      sort = sort + '&sort=' + secondarySortField + ',' + sortDirection;
    }

    return this.matchingPropertyHttpService.getPropertyMatches(page, size, sort).catch(error => {
      this.flashMessagesService.error(this.translateService.instant('MESSAGES.ERROR.GET_MATCHING_PROPERTIES'));
      this.logService.error(`Error getting Matching Properties: ${error}`);
      return Observable.throw(error);
    }).map(propertyMatches => {
      this.propertyPage.next(propertyMatches);
      return propertyMatches;
    });
  }

  assignMatchingCandidate(property: MatchingPropertyAssign): Observable<any> {
    return this.matchingPropertyHttpService.assign(property)
      .catch(error => {
        this.flashMessagesService.error(this.translateService.instant('MESSAGES.ERROR.ASSIGN_MATCHING_PROPERTIES'));
        this.logService.error(`Error assigning Matching Properties: ${error}`);
        return Observable.throw(error);
      })
      .map(result => {
        this.flashMessagesService.success('EVENT.PROPERTY_REVIEW.CANDIDATE_ASSIGNED');
        return result;
      });
  }

  addProperty(newRec: PropertyMatch): Observable<MatchingProperty> {
    return this.matchingPropertyHttpService.add(newRec)
      .catch(error => {
        this.flashMessagesService.error('MESSAGES.ERROR.ADD_PROPERTY', `Error adding property: ${error}`);
        return Observable.throw(error);
      })
      .map((res: MatchingProperty) => {
        this.flashMessagesService.success('MESSAGES.SUCCESS.ADD_PROPERTY_MATCHING');
        return res;
      });
  }

  /* getMatchingCollisionProperties(eventInfo: any): Observable<any> {
    const eventInfoList = {
      "event_info":[
        eventInfo
      ]
    }
    return this.matchingPropertyHttpService.getEventCollisionPropertyMatches(eventInfoList).catch(error => {
      this.flashMessagesService.error(this.translateService.instant('MESSAGES.ERROR.GET_MATCHING_PROPERTIES'));
      this.logService.error(`Error getting Matching Properties: ${error}`);
      return Observable.throw(error);
    }).map(propertyMatches => {
      this.propertyPage.next(propertyMatches);
      return propertyMatches;
    });
  } */

  getMatchingCollisionProperties(eventInfo: any): Observable<any> {
    return this.eventCollisionService.getEventCollisionPropertyMatches(eventInfo)
    .pipe(map((response) => response));
  }

}



// WEBPACK FOOTER //
// /var/lib/jenkins/jobs/GroupPay/jobs/QA2/jobs/QA-Pioneer-ALL-2-POC/workspace/pioneer-web/src/app/event/service/matching-property.store.ts